/** The overview (UX-SPEC §5): headline and translation axis, caveat, patterns, notices, heat strip, chapters, top channels. */
import { signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import type { ChannelId } from '../../engine/channels/types.ts';
import { hygieneLine } from '../../shared/diagnostics.ts';
import { authorshipDial, translationDial } from '../../shared/dial.ts';
import type { GlossaryKey } from '../../shared/help-types.ts';
import { figuresFor, modelCardHrefFor } from '../../shared/model-figures.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { OverviewActions } from '../bench/Actions.tsx';
import { showTranslationOption } from '../bench/translation-option.ts';
import { DataTable, type SortState } from '../components/DataTable.tsx';
import { Dial } from '../components/Dial.tsx';
import { HeatStrip, stripMarkers, stripMode } from '../components/HeatStrip.tsx';
import { ChannelTerm, Term } from '../components/Term.tsx';
import { fmt1, fmt2, fmtInt, fmtInterval } from '../format.ts';
import { derive, hotspotsBetween } from '../model.ts';
import { hashFor, route, setParams } from '../router.ts';
import { DIAL } from '../strings-dial.ts';
import {
	HEADLINE_EXPLAIN as HX,
	NOTICE_EXPLAIN as NX,
	STATUS_EXPLAIN,
	shortPointSentence,
	unreachableSentence,
} from '../strings-explain.ts';
import { glossaryFor } from '../strings-glossary.ts';
import { HELP } from '../strings-help.ts';
import {
	CHANNELS,
	HEADER,
	OVERVIEW as O,
	STRIP,
	TABLE as TB,
	TRANSLATION as TR,
} from '../strings.ts';
import { Icon, Seg, Whisker } from '../ui.tsx';
import { ClosestCategories, ReadingGuide } from './OverviewReading.tsx';

const showTable = signal(false);

/** UX-EXP-06 (Should): the headline, the translation headline and the caveat as three lines. */
function CopyHeadline({ vm }: { vm: ViewModel }) {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(
				[
					vm.ensemble.headline,
					vm.ensemble.calibratedIndex?.interval?.phrase,
					vm.ensemble.compositeHeadline,
					vm.ensemble.translationHeadline,
					vm.ensemble.caveat,
				]
					.filter((l) => l !== undefined)
					.join('\n')
			);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2500);
		} catch {
			setCopied(false);
		}
	};
	return (
		<p class="copy-line">
			<button type="button" class="btn-text" onClick={() => void copy()}>
				{HEADER.copyHeadline}
			</button>
			<span class="dim" aria-live="polite">
				{copied ? HEADER.copied : ''}
			</span>
		</p>
	);
}

const TERMS_LINE: GlossaryKey[] = [
	'calibrated',
	'calibrated-confidence',
	'reference-point',
	'reference-prior',
	'strictest-stratum',
	'length-group',
	'text-sampling-variation',
	'seed-rules-composite',
	'evidence-coverage',
];

/** The status sentence (P3): the engine's status picks the wording; the figures come from the model card. */
function statusExplain(
	ci: NonNullable<ViewModel['ensemble']['calibratedIndex']> | undefined,
	modelId: string
): string {
	if (!ci) return STATUS_EXPLAIN['not-available']('', { n: '', flagged: '', bound: 0 });
	// CD-108: a length group with its own operating point (the short-text point) has its own sentence
	const shortPoint = shortPointSentence(modelId, ci);
	if (shortPoint) return shortPoint;
	const fig = figuresFor(modelId);
	const h = fig.pooledHuman;
	const pooled = { n: fmtInt(h.n), flagged: String(h.flagged), bound: h.bound };
	let text = STATUS_EXPLAIN[ci.status](fmt1(ci.referenceIndex), pooled);
	if (ci.status === 'flagged') {
		const f = fig.finalSliceHuman;
		const w = fig.widestCell;
		if (f) text += ` ${HX.finalSlice(String(f.flagged), fmtInt(f.n), f.bound)}`;
		text += ` ${HX.widest(w.label, String(w.flagged), fmtInt(w.n), w.bound)}`;
	}
	// CD-94: a length group whose calibration map cannot reach the reference point
	const unreachable = unreachableSentence(modelId, ci.group, ci.status);
	if (unreachable) text += ` ${unreachable}`;
	return text;
}

/** The authorship dial and its large "N in 100" (UD-36); the sentence and the conditions follow in `AuthorshipReading`. */
function AuthorshipDial({ vm }: { vm: ViewModel }) {
	const e = vm.ensemble;
	const d = authorshipDial(e.calibratedIndex, e.model.id);
	return (
		<div class="dial-block" data-testid="authorship-dial-block">
			<Dial
				id="authorship"
				degrees={d.degrees}
				aria={d.aria}
				lowEnd={DIAL.humanEnd}
				highEnd={DIAL.machineEnd}
				marks={d.marks}
				interval={d.interval}
			/>
			<div
				class={`dial-figure${d.state === 'greyed' ? ' dim' : ''}`}
				data-testid="authorship-dial-figure"
			>
				{d.reading ?? DIAL.authorship.noReading}
			</div>
		</div>
	);
}

/** The reading sentence with the conditions that always stand beside it, the marks' legend and the interval note. */
function AuthorshipReading({ vm }: { vm: ViewModel }) {
	const e = vm.ensemble;
	const d = authorshipDial(e.calibratedIndex, e.model.id);
	const A = DIAL.authorship;
	if (d.state === 'greyed')
		return (
			<p class="explain" data-testid="authorship-dial-reason">
				{A.greyedLead} {d.reason}
			</p>
		);
	return (
		<div class="dial-reading" data-testid="authorship-dial-reading">
			<p class="explain" data-testid="authorship-dial-sentence">
				{d.sentence}
			</p>
			<div class="eyebrow">{A.conditionsLabel}</div>
			<ul class="dial-conditions" data-testid="authorship-dial-conditions">
				{d.conditions.map((c, i) => (
					<li key={c}>
						{c}
						{i === 1 ? (
							<>
								{' '}
								<a href={modelCardHrefFor(e.model.id)} target="_blank" rel="noreferrer noopener">
									{A.corpusLink}
								</a>
							</>
						) : null}
					</li>
				))}
			</ul>
			<ul class="dial-marks" data-testid="authorship-dial-marks">
				{d.marks.map((m) => (
					<li key={m.id}>
						<span class={`dial-swatch dial-swatch-${m.id}`} aria-hidden="true" /> {m.label}
					</li>
				))}
				{d.intervalNote ? <li>{d.intervalNote}</li> : null}
			</ul>
		</div>
	);
}

/** The smaller, uncalibrated translation dial: a measure of translation marks, never a confidence (UD-36). */
function TranslationDial({ vm }: { vm: ViewModel }) {
	const t = vm.ensemble.translation;
	const tci = t.g4 === true ? t.calibratedIndex : undefined;
	const d = translationDial(tci ? tci.index : t.score);
	const T = DIAL.translation;
	return (
		<div class="dial-block dial-block-small" data-testid="translation-dial-block">
			<Dial
				id="translation"
				small
				degrees={d.degrees}
				aria={d.aria}
				lowEnd={DIAL.translationLowEnd}
				highEnd={DIAL.translationHighEnd}
			/>
			<p class="explain" data-testid="translation-dial-label">
				{T.label}
			</p>
		</div>
	);
}

function Headline({ vm }: { vm: ViewModel }) {
	const e = vm.ensemble;
	const t = e.translation;
	const ci = e.calibratedIndex;
	// the fixed speech caveat of the analysed result's model (null removes it)
	const speech = figuresFor(e.model.id).speechCaveat;
	// the calibrated translation index is the hero only with a translation model AND gate G4 passed (T2-DESIGN §8)
	const tci = t.g4 === true ? t.calibratedIndex : undefined;
	return (
		<section class="headline" aria-labelledby="h-index">
			<h1 id="h-index" class="visually-hidden">
				{O.title}
			</h1>
			<div class="headline-grid">
				<div class="axis-block" data-testid="authorship-block">
					<div class="eyebrow">{O.authorshipTitle}</div>
					{ci ? (
						<>
							<div class="hero-row">
								<div>
									<div class="hero" aria-hidden="true" data-testid="hero-index">
										{ci.index === null ? '—' : fmt1(ci.index)}
									</div>
									<div class="hero-interval" data-testid="hero-status">
										{ci.statusPhrase}
									</div>
								</div>
								<AuthorshipDial vm={vm} />
							</div>
							<AuthorshipReading vm={vm} />
							<p class="explain" data-testid="status-explain">
								{statusExplain(ci, e.model.id)}
							</p>
							{ci.interval ? (
								<div class="hero-interval" data-testid="hero-sampling-interval">
									{ci.interval.phrase}
								</div>
							) : null}
						</>
					) : (
						<>
							<div class="hero-row">
								<div>
									<div class="hero" aria-hidden="true" data-testid="hero-index">
										{fmt1(e.index)}
									</div>
									<div class="hero-interval">
										<Term k="interval-90">{O.intervalLabel}</Term> {fmtInterval(e.ci90)}
									</div>
								</div>
								<AuthorshipDial vm={vm} />
							</div>
							<AuthorshipReading vm={vm} />
							<p class="explain" data-testid="status-explain">
								{statusExplain(undefined, e.model.id)}
							</p>
						</>
					)}
					<div class="not-lines" data-testid="not-lines">
						<div class="eyebrow">{HX.notLabel}</div>
						<p class="explain">{HX.notLines}</p>
					</div>
					<p class="verbatim" data-testid="headline">
						{e.headline}
					</p>
					{speech ? (
						<p class="explain" data-testid="speech-caveat">
							{speech.lead}{' '}
							<a href={HELP.limitationsHref} target="_blank" rel="noreferrer noopener">
								{speech.link}
							</a>
							.
						</p>
					) : null}
					{ci ? (
						<p class="explain" data-testid="calibrated-hint">
							{O.calibratedHint}
							{ci.interval ? ` ${O.intervalMeaning}` : ''}
						</p>
					) : null}
					{ci && ci.index !== null && (ci.index - 50) * (e.index - 50) < 0 ? (
						<div data-testid="reconcile">
							<div class="eyebrow">{HX.reconcileLabel}</div>
							<p class="explain">{HX.reconcile}</p>
						</div>
					) : null}
					{e.compositeHeadline ? (
						<p class="verbatim dim" data-testid="composite-headline">
							{e.compositeHeadline}
						</p>
					) : null}
					<p class="explain" title={O.coverageHelp}>
						<span class="term">
							<Term k="evidence-coverage">{O.coverageLabel}</Term> {fmt2(e.evidenceCoverage)}
						</span>
						: {O.coverageHelp}
					</p>
					<p class="explain" data-testid="terms-line">
						{HX.termsLabel}:{' '}
						{TERMS_LINE.map((k, i) => (
							<span key={k}>
								{i > 0 ? ' · ' : ''}
								<Term k={k}>{glossaryFor(k).term}</Term>
							</span>
						))}
					</p>
				</div>
				<div class="axis-block" data-testid="translation-block">
					<div class="eyebrow">{O.translationTitle}</div>
					{tci ? (
						<div class="hero-row">
							<div>
								<div class="hero hero-2" aria-hidden="true" data-testid="translation-hero">
									{tci.index === null ? '—' : fmt1(tci.index)}
								</div>
								<div class="hero-interval" data-testid="translation-status">
									{tci.statusPhrase}
								</div>
							</div>
							<TranslationDial vm={vm} />
						</div>
					) : (
						<div class="hero-row">
							<div>
								<div class="hero hero-2" aria-hidden="true">
									{t.score === null ? '—' : fmt1(t.score)}
								</div>
								<div class="hero-interval">
									{t.ci90 ? `${O.intervalLabel} ${fmtInterval(t.ci90)}` : ' '}
								</div>
							</div>
							<TranslationDial vm={vm} />
						</div>
					)}
					<p class="verbatim" data-testid="translation-headline">
						{e.translationHeadline}
					</p>
					<p class="explain" data-testid="translation-note">
						{tci ? TR.calibratedHint : TR.axisNote} {TR.scope}
					</p>
					<p class="explain">{HX.translationRead}</p>
					{t.score === null ? <p class="explain">{HX.translationNotAvailable}</p> : null}
					<p class="explain">
						<a href={hashFor('channel', { id: 'MT-01' })}>{O.translationLink}</a>
						{' · '}
						<a href={hashFor('channels')}>{O.translationChannels}</a>
					</p>
					{t.pattern ? (
						<div class="pattern" data-testid="translation-pattern">
							<PatternBlock p={t.pattern} />
						</div>
					) : null}
				</div>
			</div>
			<p class="caveat" data-testid="caveat">
				{e.caveat}
			</p>
			<CopyHeadline vm={vm} />
		</section>
	);
}

function PatternBlock({ p }: { p: ViewModel['ensemble']['patterns'][number] }) {
	return (
		<div class="pattern-block">
			<strong>
				{p.name}
				{p.value ? ` (${p.value})` : ''}
			</strong>
			<p class="dim">{O.patternLead}</p>
			<ul>
				{p.triggers.map((t) => (
					<li key={t}>{t}</li>
				))}
			</ul>
			{p.kind === 'assisted' ? <p class="dim">{O.assistFloor}</p> : null}
		</div>
	);
}

function Patterns({ vm }: { vm: ViewModel }) {
	const ps = vm.ensemble.patterns;
	return (
		<section aria-labelledby="h-patterns">
			<h2 id="h-patterns">{O.patternsTitle}</h2>
			{ps.length === 0 ? (
				<>
					<p data-testid="patterns-none">{O.patternsNone}</p>
					<p class="dim">{NX.patternsNone}</p>
				</>
			) : (
				<div data-testid="patterns">
					<p class="dim">{NX.patternsSome}</p>
					{ps.map((p) => (
						<PatternBlock key={`${p.kind}-${p.name}`} p={p} />
					))}
				</div>
			)}
			<OverviewActions showTranslation={showTranslationOption(vm.ensemble)} />
		</section>
	);
}

function Notices({ vm }: { vm: ViewModel }) {
	const d = derive(vm);
	const e = vm.ensemble;
	const withheld = e.hotspots.withheld;
	const reason = withheld[0]?.reason;
	const hyg = hygieneLine(e.hygiene);
	return (
		<section aria-labelledby="h-notices">
			<h2 id="h-notices" class="visually-hidden">
				{O.noticesTitle}
			</h2>
			<ul class="notices" data-testid="notices">
				{withheld.length > 0 ? (
					<li class="notice">
						<Icon name="notice" />
						<span>
							{O.withheld(withheld.map((w) => w.id).join(', '))}{' '}
							<details class="inline">
								<summary>{O.reason}</summary>
								<span class="dim">{reason}</span>
							</details>
							<span class="dim notice-explain">{NX.withheld}</span>
						</span>
					</li>
				) : null}
				{d.lengthDependent.length > 0 ? (
					<li class="notice">
						<Icon name="notice" />
						<span>
							{O.lengthDependent(d.lengthDependent.join(' and '))}
							<span class="dim notice-explain">{NX.lengthDependent}</span>
						</span>
					</li>
				) : null}
				{e.abstained.length > 0 ? (
					<li class="notice">
						<Icon name="notice" />
						<span>
							{O.abstained(e.abstained.length)}.{' '}
							<a href={hashFor('diagnostics')}>{O.seeDiagnostics}</a>
							<span class="dim notice-explain">{NX.abstained}</span>
						</span>
					</li>
				) : null}
				{vm.meta.warnings.map((w) => (
					<li class="notice" key={w}>
						<Icon name="notice" />
						<span>
							{w}
							<span class="dim notice-explain">{NX.warning}</span>
						</span>
					</li>
				))}
				{hyg ? (
					<li class="notice" data-testid="hygiene-notice">
						<Icon name="notice" />
						<span>
							{hyg}
							<span class="dim notice-explain">{NX.hygiene}</span>
						</span>
					</li>
				) : null}
				{!vm.meta.model.calibrated ? (
					<li class="info">
						<Icon name="info" />
						<span>
							{O.uncalibrated(vm.meta.model.id)}
							<span class="dim notice-explain">{NX.uncalibrated}</span>
						</span>
					</li>
				) : null}
			</ul>
		</section>
	);
}

function StripSection({ vm }: { vm: ViewModel }) {
	const open = showTable.value || route.value.params.get('table') === '1';
	useEffect(() => {
		if (route.value.params.get('table') === '1') {
			showTable.value = true;
			setParams({ table: undefined });
			document.getElementById('h-strip')?.scrollIntoView();
		}
	}, []);
	const mode = stripMode.value;
	const joint = vm.ensemble.segments.joint;
	return (
		<section aria-labelledby="h-strip">
			<div class="sec-head">
				<h2 id="h-strip">{O.stripTitle}</h2>
				<Seg label={O.stripTitle}>
					<button
						type="button"
						class={`seg-btn${mode === 'section' ? ' is-on' : ''}`}
						aria-pressed={mode === 'section'}
						onClick={() => {
							stripMode.value = 'section';
						}}
					>
						{STRIP.bySection}
					</button>
					<button
						type="button"
						class={`seg-btn${mode === 'chapter' ? ' is-on' : ''}`}
						aria-pressed={mode === 'chapter'}
						onClick={() => {
							stripMode.value = 'chapter';
						}}
					>
						{STRIP.byChapter}
					</button>
					<button
						type="button"
						class={`seg-btn${stripMarkers.value ? ' is-on' : ''}`}
						aria-pressed={stripMarkers.value}
						onClick={() => {
							stripMarkers.value = !stripMarkers.value;
						}}
					>
						{STRIP.markers}
					</button>
				</Seg>
				<button
					type="button"
					class="btn-text"
					aria-expanded={open}
					onClick={() => {
						showTable.value = !open;
					}}
				>
					{open ? STRIP.hideTable : STRIP.showTable}
				</button>
			</div>
			<HeatStrip vm={vm} variant="section" />
			<p class="caption" id="cap-strip">
				{O.stripCaption}
			</p>
			<p class="caption" data-testid="strip-explain">
				{HX.strip}
			</p>
			{open ? (
				<div data-testid="strip-table">
					<DataTable
						caption={STRIP.tableCaption}
						rows={joint}
						rowKey={(s) => s.ordinal}
						columns={[
							{ id: 'n', header: TB.number, render: (s) => s.ordinal + 1, num: true },
							{ id: 'words', header: TB.words, render: (s) => fmtInt(s.words), num: true },
							{ id: 'index', header: TB.index, render: (s) => fmt1(s.index), num: true },
							{ id: 'ci', header: TB.interval, render: (s) => fmtInterval(s.ci90), num: true },
							{ id: 'm', header: TB.method, render: (s) => s.ciMethod },
							{
								id: 'h',
								header: TB.hotspots,
								render: (s) => fmtInt(hotspotsBetween(vm, s.range.paraStart, s.range.paraEnd)),
								num: true,
							},
						]}
					/>
				</div>
			) : null}
		</section>
	);
}

type ChapSort = 'position' | 'index' | 'hotspots';

function Chapters({ vm }: { vm: ViewModel }) {
	const units = vm.ensemble.segments.units;
	const [sort, setSort] = useState<SortState>({ id: 'n', dir: 'asc' });
	const rows = units.map((u) => ({
		u,
		hot: hotspotsBetween(vm, u.range.paraStart, u.range.paraEnd),
	}));
	const key: ChapSort =
		sort?.id === 'index' ? 'index' : sort?.id === 'hot' ? 'hotspots' : 'position';
	const sorted =
		key === 'position'
			? sort?.dir === 'desc'
				? [...rows].reverse()
				: rows
			: [...rows].sort((a, b) => {
					const d = key === 'index' ? a.u.index - b.u.index : a.hot - b.hot;
					return sort?.dir === 'asc' ? d : -d;
				});
	const onSort = (id: string) =>
		setSort(
			sort?.id === id
				? { id, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
				: { id, dir: id === 'n' ? 'asc' : 'desc' }
		);
	const label = units[0]?.kind === 'scene' ? 'Scenes' : O.chaptersTitle;
	return (
		<section aria-labelledby="h-chapters">
			<div class="sec-head">
				<h2 id="h-chapters">{label}</h2>
				<span class="caption">{O.chaptersCaption}</span>
			</div>
			<DataTable
				testId="chapters-table"
				caption={label}
				rows={sorted}
				rowKey={(r) => r.u.ordinal}
				virtual={sorted.length > 200}
				sort={sort}
				onSort={onSort}
				columns={[
					{ id: 'n', header: TB.number, sortable: true, num: true, render: (r) => r.u.ordinal + 1 },
					{
						id: 'title',
						header: TB.title,
						render: (r) => (
							<a href={hashFor('read', { params: { p: String(r.u.range.paraStart) } })}>
								{r.u.title ?? `${STRIP.chapter(r.u.ordinal + 1)}`}
							</a>
						),
					},
					{ id: 'words', header: TB.words, num: true, render: (r) => fmtInt(r.u.words) },
					{
						id: 'index',
						header: TB.index,
						sortable: true,
						num: true,
						render: (r) => fmt1(r.u.index),
					},
					{
						id: 'ci',
						header: TB.interval,
						render: (r) => (
							<span class="ci-cell">
								<span class="num">{fmtInterval(r.u.ci90)}</span>
								<Whisker value={r.u.index} lo={r.u.ci90[0]} hi={r.u.ci90[1]} width={96} />
							</span>
						),
					},
					{
						id: 'm',
						header: <Term k="interval-method">{TB.method}</Term>,
						render: (r) => r.u.ciMethod,
					},
					{
						id: 'hot',
						header: TB.hotspots,
						sortable: true,
						num: true,
						render: (r) => fmtInt(r.hot),
					},
				]}
			/>
		</section>
	);
}

export function littleEvidence(kappa: number | undefined): boolean {
	return kappa !== undefined && kappa < 0.2;
}

function Strongest({ vm }: { vm: ViewModel }) {
	const d = derive(vm);
	const rows = vm.ensemble.channels
		.filter((c) => c.status === 'scored' && c.axis === 'authorship' && c.score !== undefined)
		.sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
		.slice(0, 8);
	return (
		<section aria-labelledby="h-strong">
			<div class="sec-head">
				<h2 id="h-strong">{O.strongestTitle}</h2>
				<a href={hashFor('channels')}>{O.allChannels}</a>
			</div>
			<p class="caption">{O.strongestCaption}</p>
			<p class="caption" data-testid="strongest-explain">
				{HX.strongest}
			</p>
			<DataTable
				testId="strongest-table"
				caption={O.strongestTitle}
				rows={rows}
				rowKey={(c) => c.id}
				columns={[
					{
						id: 'id',
						header: CHANNELS.cols.id,
						mono: true,
						render: (c) => <a href={hashFor('channel', { id: c.id })}>{c.id}</a>,
					},
					{
						id: 'name',
						header: CHANNELS.cols.name,
						render: (c) => <ChannelTerm id={c.id} name={c.name} />,
					},
					{
						id: 'score',
						header: CHANNELS.cols.score,
						num: true,
						render: (c) => fmt1(c.score ?? 0),
					},
					{
						id: 'ci',
						header: <Term k="interval-90">{CHANNELS.cols.interval}</Term>,
						render: (c) =>
							c.ci90 ? (
								<span class="ci-cell">
									<span class="num">{fmtInterval(c.ci90)}</span>
									<Whisker
										value={c.score ?? 0}
										lo={c.ci90[0]}
										hi={c.ci90[1]}
										width={96}
										hollow={littleEvidence(c.kappa)}
									/>
								</span>
							) : (
								''
							),
					},
					{
						id: 'kappa',
						header: <Term k="kappa">{CHANNELS.cols.kappa}</Term>,
						num: true,
						render: (c) => fmt2(c.kappa ?? 0),
					},
					{
						id: 'notes',
						header: CHANNELS.cols.notes,
						render: (c) => (
							<ChannelNotes
								id={c.id}
								notes={c.notes}
								kappa={c.kappa}
								lengthNoteFor={d.lengthDependent}
							/>
						),
					},
				]}
			/>
		</section>
	);
}

/** Notes beside a score: the engine's notes verbatim (ED-14 on the same line), and "little evidence" for κ < 0.2. */
export function ChannelNotes({
	id,
	notes,
	kappa,
}: { id: ChannelId; notes: string[]; kappa?: number; lengthNoteFor?: ChannelId[] }) {
	return (
		<span class="notes-cell">
			{littleEvidence(kappa) ? <span class="note">{CHANNELS.littleEvidence}</span> : null}
			{notes.map((n) => (
				<span key={`${id}-${n}`} class={n.startsWith('length-dependent') ? 'note-notice' : 'note'}>
					{n}
				</span>
			))}
		</span>
	);
}

export function Overview({ vm }: { vm: ViewModel }) {
	return (
		<div class="wrap" data-testid="overview">
			<Headline vm={vm} />
			<ReadingGuide vm={vm} />
			<ClosestCategories vm={vm} />
			<Patterns vm={vm} />
			<Notices vm={vm} />
			<StripSection vm={vm} />
			<Chapters vm={vm} />
			<Strongest vm={vm} />
		</div>
	);
}
